import { collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc, Timestamp, deleteDoc, runTransaction, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { logReservationCreated, logReservationAction, getReservationWorkflowLogByReservation } from '../logging/reservationWorkflowLogger';
import { logAuditAction, logFieldUpdate, logStatusChange } from '../logging/auditLogger';
import { NOTIFICATION_TYPES } from '../../constants/notifications';
import { createNotificationsForChange } from './notificationService';
import { RESERVATION_WORKFLOW_ACTIONS } from '../../constants/systemConfig';

const getCollectionPath = (tenantId) => `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS}`;

/**
 * Generate a unique sequential reservation number (e.g., REZ-100123)
 */
const generateReservationNumber = async (tenantId) => {
    // Keep counters separate? Or move to tenant?
    // Moving to tenant-specific counter for better isolation: tenants/{tenantId}/counters/reservation
    const counterRef = doc(db, `tenants/${tenantId}/counters`, 'reservation');

    try {
        return await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);

            let newCount = 100000;
            if (counterDoc.exists()) {
                newCount = counterDoc.data().current + 1;
            }

            transaction.set(counterRef, { current: newCount }, { merge: true });

            return `REZ-${newCount}`;
        });
    } catch (error) {
        console.error("Counter transaction failed:", error);
        return `REZ-${Math.floor(100000 + Math.random() * 900000)}`;
    }
};

/**
 * Get reservations for a specific date range (start of day to end of day)
 */
export const getDailyReservations = async (tenantId, dateStr) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');

        const startOfDay = new Date(dateStr);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(dateStr);
        endOfDay.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, getCollectionPath(tenantId)),
            where('startTime', '>=', Timestamp.fromDate(startOfDay)),
            where('startTime', '<=', Timestamp.fromDate(endOfDay))
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            startTime: doc.data().startTime.toDate(),
            endTime: doc.data().endTime.toDate(),
            actualStartTime: doc.data().actualStartTime ? doc.data().actualStartTime.toDate() : null
        }));
    } catch (error) {
        console.error('Error fetching reservations:', error);
        throw error;
    }
};

/**
 * Get reservations for a specific date range (start date to end date)
 */
export const getReservationsByDateRange = async (tenantId, startDateStr, endDateStr) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');

        const startOfDay = new Date(startDateStr);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(endDateStr);
        endOfDay.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, getCollectionPath(tenantId)),
            where('startTime', '>=', Timestamp.fromDate(startOfDay)),
            where('startTime', '<=', Timestamp.fromDate(endOfDay))
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            startTime: doc.data().startTime.toDate(),
            endTime: doc.data().endTime.toDate(),
            actualStartTime: doc.data().actualStartTime ? doc.data().actualStartTime.toDate() : null
        }));
    } catch (error) {
        console.error('Error fetching reservations range:', error);
        throw error;
    }
};

/**
 * Get waiting reservations (unscheduled)
 */
export const getWaitingReservations = async (tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');

        const q = query(
            collection(db, getCollectionPath(tenantId)),
            where('dockId', '==', null)
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // startTime/endTime might be null here
            startTime: doc.data().startTime ? doc.data().startTime.toDate() : null,
            endTime: doc.data().endTime ? doc.data().endTime.toDate() : null,
            actualStartTime: doc.data().actualStartTime ? doc.data().actualStartTime.toDate() : null
        }));
    } catch (error) {
        console.error('Error fetching waiting reservations:', error);
        throw error;
    }
};

/**
 * Create a new reservation
 */
export const createReservation = async (data, performedBy) => {
    try {
        if (!data.tenantId) throw new Error('Tenant ID is required in reservation data');

        // Generate Human Readable ID
        const reservationNumber = await generateReservationNumber(data.tenantId);

        const docRef = await addDoc(collection(db, getCollectionPath(data.tenantId)), {
            ...data,
            reservationNumber,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            createdBy: performedBy?.id || performedBy?.uid || null,
            creatorName: performedBy?.fullName || performedBy?.displayName || performedBy?.name || performedBy?.email || 'System',
            creatorRole: performedBy?.role || null,
            startTime: data.startTime ? Timestamp.fromDate(data.startTime) : null,
            endTime: data.endTime ? Timestamp.fromDate(data.endTime) : null
        });

        // Log creation workflow
        if (performedBy) {
            await logReservationCreated(docRef.id, data.tenantId, performedBy, { ...data, reservationNumber });

            // Unified Audit Log (entityId, entityType, actionType, changes, performedBy, context, tenantId)
            await logAuditAction(docRef.id, 'RESERVATION', 'CREATE', { ...data, reservationNumber }, performedBy, 'ReservationCreate', data.tenantId);
        }

        return docRef.id;
    } catch (error) {
        console.error('Error creating reservation:', error);
        throw error;
    }
};

/**
 * Update a reservation (for Drag & Drop or Edit)
 */
export const updateReservation = async (id, data, performedBy, tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required for updating reservation');

        // 1. Fetch old data for comparison
        const docRef = doc(db, getCollectionPath(tenantId), id);
        const docSnap = await getDoc(docRef);
        const oldData = docSnap.exists() ? docSnap.data() : {};

        const updateData = {
            ...data,
            updatedAt: Timestamp.now()
        };

        if (data.startTime) updateData.startTime = Timestamp.fromDate(data.startTime);
        if (data.endTime) updateData.endTime = Timestamp.fromDate(data.endTime);

        // Remove undefined fields to prevent Firestore errors
        Object.keys(updateData).forEach(key => {
            if (updateData[key] === undefined) {
                delete updateData[key];
            }
        });

        // 2. Perform Update
        await updateDoc(docRef, updateData);

        // 3. Log Changes (Audit Log)
        if (performedBy) {
            // Check for Status Change
            if (data.status && data.status !== oldData.status) {
                await logStatusChange(id, 'RESERVATION', oldData.status, data.status, performedBy, 'UpdateReservation', tenantId);
            }

            // Check for Time Changes specifically
            const formatTime = (date) => {
                if (!date) return 'N/A';
                try {
                    const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
                    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                } catch (e) { return 'Invalid Date'; }
            };

            const isTimeChanged = data.startTime && formatTime(oldData.startTime) !== formatTime(data.startTime);

            if (isTimeChanged) {
                await logAuditAction(id, 'RESERVATION', 'TIME_CHANGED', {
                    old: formatTime(oldData.startTime),
                    new: formatTime(data.startTime)
                }, performedBy, 'UpdateReservation', tenantId);
            }

            // Check for Other Field Updates
            const fieldChanges = {};
            //  Helper to safely stringify for comparison
            const normalize = (v) => {
                if (v === null || v === undefined || v === '') return '';
                if (typeof v === 'object') {
                    try {
                        if (v.id) return v.id;
                        if (v.name) return v.name;
                        return JSON.stringify(v);
                    } catch (e) { return String(v); }
                }
                return String(v);
            };

            Object.keys(data).forEach(key => {
                if (['status', 'updatedAt', 'startTime', 'endTime', 'tenantId', 'id'].includes(key)) return;
                // Skip complex objects or array comparisons if not needed for notification triggers (handled above)
                // Just logging generic updates here

                const oldVal = normalize(oldData[key]);
                const newVal = normalize(data[key]);

                if (oldVal !== newVal) {
                    fieldChanges[key] = { old: oldVal, new: newVal };
                }
            });

            if (Object.keys(fieldChanges).length > 0) {
                await logAuditAction(id, 'RESERVATION', 'UPDATE_FIELD', fieldChanges, performedBy, 'UpdateReservation', tenantId);
            }

            // --- NOTIFICATION TRIGGER ---
            const notificationChanges = {};

            // Status Change
            if (data.status && data.status !== oldData.status) {
                notificationChanges[NOTIFICATION_TYPES.STATUS_CHANGE] = {
                    old: oldData.status,
                    new: data.status
                };
            }

            // Time Change
            if (isTimeChanged) {
                notificationChanges[NOTIFICATION_TYPES.TIME_CHANGE] = {
                    old: formatTime(oldData.startTime),
                    new: formatTime(data.startTime)
                };
            }

            // Plate Change
            if (data.plateNumber && data.plateNumber !== oldData.plateNumber) {
                notificationChanges[NOTIFICATION_TYPES.PLATE_CHANGE] = {
                    old: oldData.plateNumber || '-',
                    new: data.plateNumber
                };
            }

            // Driver Change
            const driverName = (d) => d?.fullName || d?.name || '-';
            if (data.driver && driverName(data.driver) !== driverName(oldData.driver)) {
                notificationChanges[NOTIFICATION_TYPES.DRIVER_CHANGE] = {
                    old: driverName(oldData.driver),
                    new: driverName(data.driver)
                };
            }

            // Ramp (Dock) Change
            if ('dockId' in data && data.dockId !== oldData.dockId) {
                notificationChanges[NOTIFICATION_TYPES.RAMP_CHANGE] = {
                    old: oldData.dockId || null,
                    new: data.dockId || null
                };
            }

            if (Object.keys(notificationChanges).length > 0) {
                const fullReservation = { ...oldData, ...updateData, id };
                await createNotificationsForChange(tenantId, fullReservation, notificationChanges, performedBy);
            }
            // ----------------------------
        }

        // Log legacy workflow action
        if (performedBy && data.status) {
            try {
                // We need to find the active log for this reservation
                const log = await getReservationWorkflowLogByReservation(id, tenantId);

                if (log) {
                    await logReservationAction(log.id, {
                        action: RESERVATION_WORKFLOW_ACTIONS.STATUS_CHANGED || 'STATUS_CHANGED',
                        performedBy,
                        newStatus: data.status,
                        changes: data
                    }, tenantId);
                }
            } catch (logError) {
                console.warn('Logging failed during reservation update:', logError);
                // Do not throw, allow the status update to proceed
            }
        }
    } catch (error) {
        console.error('Error updating reservation:', error);
        throw error;
    }
};

/**
 * Add a note to a reservation without changing its fields
 */
export const addReservationNote = async (id, note, performedBy, tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');

        const log = await getReservationWorkflowLogByReservation(id, tenantId);
        if (log) {
            await logReservationAction(log.id, {
                action: RESERVATION_WORKFLOW_ACTIONS.NOTE_ADDED || 'NOTE_ADDED',
                performedBy,
                notes: note,
                changes: { note }
            }, tenantId);
        }

        // Also update the reservation document itself to show indicator and for quick access
        const resRef = doc(db, getCollectionPath(tenantId), id);
        const docSnap = await getDoc(resRef);
        const oldData = docSnap.exists() ? docSnap.data() : null;

        await updateDoc(resRef, {
            note: note,
            lastNoteAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        });

        // Also log to audit_logs for the history modal
        await logAuditAction(id, 'RESERVATION', 'NOTE_ADDED', { note }, performedBy, 'AddNote', tenantId);

        // Notify users
        if (oldData) {
            const notificationChanges = {
                [NOTIFICATION_TYPES.NOTE_ADDED]: {
                    old: oldData.note || '',
                    new: note
                }
            };
            const fullReservation = { ...oldData, id, note };
            await createNotificationsForChange(tenantId, fullReservation, notificationChanges, performedBy);
        }
    } catch (error) {
        console.error('Error adding reservation note:', error);
        throw error;
    }
};

/**
 * Delete a reservation
 */
export const deleteReservation = async (id, tenantId) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');
        await deleteDoc(doc(db, getCollectionPath(tenantId), id));
    } catch (error) {
        console.error('Error deleting reservation:', error);
        throw error;
    }
};

/**
 * Get unique values for a specific field across all reservations (for autocomplete)
 */
export const getUniqueValues = async (tenantId, field) => {
    try {
        if (!tenantId) throw new Error('Tenant ID is required');

        const q = query(
            collection(db, getCollectionPath(tenantId))
        );

        const snapshot = await getDocs(q);
        const values = new Set();
        snapshot.docs.forEach(doc => {
            const val = doc.data()[field];
            if (val) values.add(val);
        });

        return Array.from(values);
    } catch (error) {
        console.error(`Error fetching unique ${field}:`, error);
        return [];
    }
};
